import { useEffect, useState } from "react";
import { Satellite, CheckCircle2, AlertTriangle, Radio } from "lucide-react";
import { fetchTgHealth } from "../lib/api";
import { formatTimeAgo } from "../lib/utils";

/**
 * Compact GPS health pill — shows when the Telematics Guru poller last
 * succeeded and how many trackers are stale (haven't reported in 30 min).
 * Polls /integration/telematics-guru/health every 30s.
 *
 * Use it in page headers (Fleet, Dashboard) so any staff member can confirm
 * the live GPS feed at a glance.
 */
export default function GpsHealthBadge() {
  const [h, setH] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await fetchTgHealth();
        if (!cancelled) setH(data);
      } catch {
        if (!cancelled) setH(null);
      }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!h) {
    return (
      <div
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 text-xs font-bold uppercase tracking-wider"
        data-testid="gps-health-badge"
      >
        <Radio className="h-3.5 w-3.5" /> GPS · …
      </div>
    );
  }

  const ok = h.healthy && h.last_sync_status === "ok";
  const stale = h.stale_truck_count;
  const ago = h.last_sync_at ? formatTimeAgo(h.last_sync_at) : "never";

  // Three states:
  //   • Healthy + 0 stale  → green
  //   • Healthy + N stale  → amber (poller ok but some trackers haven't checked in)
  //   • Unhealthy          → red (poller behind / error)
  let style, Icon;
  if (!ok) {
    style = "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/50 dark:text-rose-200 dark:border-rose-800";
    Icon = AlertTriangle;
  } else if (stale > 0) {
    style = "bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-800";
    Icon = AlertTriangle;
  } else {
    style = "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-800";
    Icon = CheckCircle2;
  }

  const tooltip = ok
    ? `Last TG sync ${ago} · ${h.last_positions_updated}/${h.last_assets_synced} trackers reporting${
        stale > 0 ? ` · ${stale} stale (>30 min)` : ""
      }`
    : `GPS sync failing: ${h.last_sync_error || "unknown"}`;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-bold uppercase tracking-wider ${style}`}
      title={tooltip}
      data-testid="gps-health-badge"
    >
      <Satellite className="h-3.5 w-3.5" />
      <span>GPS · {ago}</span>
      {stale > 0 && (
        <span className="ml-1 normal-case font-semibold flex items-center gap-1">
          <Icon className="h-3 w-3" />
          {stale} stale
        </span>
      )}
    </div>
  );
}
